import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { COUNTRIES_COUNT, YEARS_OF_TRUST } from "@/lib/siteStats";
import {
  Phone,
  Trophy,
  Sun,
  Tag,
  ChevronDown,
  ChevronRight,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Linkedin,
  MessageCircle,
  ArrowRight,
  ArrowUpRight,
  Play,
  Heart,
  Users,
  Compass,
  Sparkles,
  Church,
  Flower2,
  Mountain,
  Camera,
  Briefcase,
  Tent,
  CalendarCheck,
  Car,
  Ship,
  Map,
  Plane,
  Crown,
  Palmtree,
  Leaf,
  Landmark,
  ShieldCheck,
  Award,
  Headphones,
  Globe,
  Clock,
  Sparkle,
  TrendingUp,
  Menu,
  X,
} from "lucide-react";
import { BRAND, TOP_UTILITY, NAV_LINKS, VALUE_PROPS, COUNTRY_FLAGS } from "@/data/content";
import { resolveAssetPath } from "@/lib/assets";
import { SearchBox } from "@/components/SearchBox";

const withQ = (to, q) => `${to}?q=${encodeURIComponent(q.replace(/^All of /, ""))}`;
const withItem = (to, item) => `${to}?item=${encodeURIComponent(item)}`;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const VP_ICONS = { Trophy, Sun, Tag };
const THEME_ICONS = {
  Heart, Users, Compass, Sparkles, Church, "Flower2": Flower2, Mountain, Camera,
  Briefcase, Tent, CalendarCheck, Car, Ship, Map, Plane, Crown, Palmtree, Leaf, Landmark,
};

const SOCIAL_LINKS = {
  whatsapp: "#",
  facebook: "https://www.facebook.com/RoyaltoursGorakhpur",
  instagram: "https://www.instagram.com/royal.holidayz",
  twitter: "https://x.com/RoyalTourTrave3",
  youtube: "https://www.youtube.com/@royalholidayspvt",
  linkedin: "https://in.linkedin.com/in/royal-holidays-507020234",
  pinterest: "https://in.pinterest.com/RoyaltoursGorakhpur/",
};

// Shared bottom strip — stats + curator + offer
const MegaFooter = () => (
  <div className="bg-gradient-to-r from-[var(--navy)] via-[#11305a] to-[var(--navy)] text-white px-7 py-3.5 flex items-center justify-between gap-6 flex-wrap">
    <div className="flex items-center gap-6 text-[11px] flex-wrap">
      <span className="inline-flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-[var(--yellow)]" />
        IATA Accredited
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Globe className="w-3.5 h-3.5 text-[var(--yellow)]" />
        {COUNTRIES_COUNT} Countries
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Award className="w-3.5 h-3.5 text-[var(--yellow)]" />
        {YEARS_OF_TRUST}+ Years of Trust
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Sparkle className="w-3.5 h-3.5 text-[var(--yellow)]" />
        ★ 4.9 · 12k+ guests
      </span>
    </div>
    <a
      href={`tel:${BRAND.phone.replace(/[^0-9+]/g, "")}`}
      className="group/cur inline-flex items-center gap-2.5 bg-white/10 hover:bg-[var(--yellow)] hover:text-[var(--navy)] rounded-full pl-2 pr-4 py-1 transition-colors"
    >
      <span className="w-7 h-7 rounded-full bg-[var(--yellow)] text-[var(--navy)] flex items-center justify-center">
        <Headphones className="w-3.5 h-3.5" />
      </span>
      <span className="leading-tight">
        <span className="block text-[9px] uppercase tracking-widest opacity-80">
          Talk to a curator
        </span>
        <span className="block text-[12px] font-semibold">{BRAND.phone}</span>
      </span>
    </a>
  </div>
);

// Promo ribbon at top of mega
const PromoRibbon = ({ children }) => (
  <div className="bg-gradient-to-r from-[var(--red)] via-[#d4202f] to-[var(--red)] text-white text-[11px] font-semibold uppercase tracking-[0.18em] text-center py-1.5 flex items-center justify-center gap-2">
    <Sparkles className="w-3 h-3 text-[var(--yellow)]" />
    {children}
    <Sparkles className="w-3 h-3 text-[var(--yellow)]" />
  </div>
);

const distributeGroups = (groups) => {
  const columnCount = groups.length >= 9 ? 4 : groups.length >= 5 ? 3 : 2;
  const columns = Array.from({ length: columnCount }, () => []);
  const weights = Array(columnCount).fill(0);

  groups.forEach((group) => {
    const weight = Math.max(group.cities.length, 1) + 2;
    const lightestColumnIdx = weights.indexOf(Math.min(...weights));
    columns[lightestColumnIdx].push(group);
    weights[lightestColumnIdx] += weight;
  });

  return columns.filter((column) => column.length > 0);
};

// ───────────────────────── Destinations Mega (India / World)
const DestinationsMega = ({ mega, promo, to = "/india" }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = mega.regions[activeIdx];
  const groupColumns = distributeGroups(active.groups);
  const denseLayout = groupColumns.length >= 4;

  return (
    <div
      data-testid="mega-panel-destinations"
      className="invisible pointer-events-none opacity-0 translate-y-1 group-hover:visible group-hover:pointer-events-auto group-hover:opacity-100 group-hover:translate-y-0 absolute left-0 right-0 top-full transition-all duration-200 z-50"
    >
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        <div className="bg-white text-[var(--navy)] shadow-[0_30px_60px_-15px_rgba(11,37,69,0.4)] rounded-b-xl border-t-[3px] border-[var(--yellow-2)] overflow-hidden">
        {promo && <PromoRibbon>{promo}</PromoRibbon>}

        {/* Top recommended chips */}
        <div className="px-7 py-4 bg-gradient-to-r from-[#fffaf0] via-white to-white border-b border-gray-100 flex flex-wrap items-center gap-x-6 gap-y-2">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--yellow-2)] font-bold">
            ★ Top Recommended
          </span>
          {mega.topRow.map((t) => (
            <Link
              key={t}
              to={withQ(to, t)}
              data-testid={`mega-top-${slug(t)}`}
              className="text-[12.5px] font-medium text-[var(--navy)] hover:text-[var(--yellow-2)] transition relative after:content-[''] after:absolute after:left-0 after:-bottom-0.5 after:h-px after:w-0 hover:after:w-full after:bg-[var(--yellow-2)] after:transition-all"
            >
              {t}
            </Link>
          ))}
        </div>

        {/* Body */}
        <div className="grid grid-cols-12">
          {/* Left: regions list */}
          <div className="col-span-3 bg-gradient-to-b from-[#f8fafc] to-[#eef2f7] border-r border-gray-100 py-5">
            {mega.regions.map((r, i) => (
              <button
                key={r.name}
                type="button"
                onMouseEnter={() => setActiveIdx(i)}
                onFocus={() => setActiveIdx(i)}
                data-testid={`mega-region-${i}`}
                className={`w-full text-left px-7 py-3 text-[13.5px] flex items-center justify-between transition-all duration-200 relative ${
                  activeIdx === i
                    ? "bg-white text-[var(--navy)] font-semibold shadow-sm"
                    : "text-[#0b2545]/70 hover:text-[var(--navy)] hover:bg-white/50"
                }`}
              >
                <span className={`absolute left-0 top-0 bottom-0 w-1 transition-all ${activeIdx === i ? "bg-[var(--yellow-2)]" : "bg-transparent"}`} />
                <span className="flex items-center gap-2">
                  {r.name}
                  {r.trending && (
                    <span className="inline-flex items-center gap-0.5 bg-[#e11d2e]/10 text-[var(--red)] text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                      <TrendingUp className="w-2.5 h-2.5" /> Hot
                    </span>
                  )}
                </span>
                <ChevronRight className={`w-3.5 h-3.5 transition-transform ${activeIdx === i ? "text-[var(--yellow-2)] translate-x-0.5" : "opacity-50"}`} />
              </button>
            ))}
            <Link
              to={to}
              className="mx-5 mt-5 inline-flex items-center justify-between gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-[var(--navy)] hover:text-[var(--yellow-2)] transition px-3 py-2 border border-[#f59e0b]/30 hover:border-[var(--yellow-2)] rounded"
            >
              View all
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            {mega.feature && (
              <Link
                to={withQ(to, mega.feature.title)}
                className="group/feat mx-5 mt-4 block relative rounded-lg overflow-hidden ring-1 ring-gray-200/60 hover:ring-[#f59e0b]/60 transition"
              >
                <img
                  src={resolveAssetPath(mega.feature.image)}
                  alt={mega.feature.title}
                  className="w-full h-28 object-cover group-hover/feat:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                  <span className="bg-[var(--yellow)] text-[var(--navy)] text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                    Featured
                  </span>
                  <span className="bg-white/90 text-[var(--navy)] text-[9px] font-semibold inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded">
                    <Clock className="w-2.5 h-2.5" /> Best Apr-Sep
                  </span>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                  <div className="font-serif-display text-sm leading-tight">
                    {mega.feature.title}
                  </div>
                  <div className="text-[10px] text-white/90 mt-0.5 flex items-center gap-1">
                    {mega.feature.subtitle}
                    <ArrowRight className="w-3 h-3 group-hover/feat:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            )}
          </div>

          {/* Middle: groups for active region */}
          <div className="col-span-9 p-6 max-h-[calc(100vh-300px)] overflow-y-auto thin-scroll">
            <div className={`grid ${denseLayout ? "grid-cols-4" : "grid-cols-3"} gap-x-6 gap-y-5 items-start`}>
              {groupColumns.map((column, columnIdx) => (
                <div key={`group-column-${columnIdx}`} className="space-y-5 min-w-0">
                  {column.map((g) => (
                    <div key={g.state} className="min-w-0">
                      <div className="flex items-center gap-2 border-b border-gray-200 pb-2 mb-3">
                        <span className="w-1 h-4 bg-[var(--yellow-2)] rounded-sm" />
                        <Link to={withQ(to, g.state)} data-testid={`mega-state-${slug(g.state)}`} className="font-serif-display text-[15px] text-[var(--navy)] hover:text-[var(--yellow-2)] transition leading-none flex-1">
                          {g.state}
                        </Link>
                      </div>
                      {g.cities.length > 0 ? (
                        <ul className="space-y-2">
                          {g.cities.map((c) => (
                            <li key={c}>
                              <Link
                                to={withQ(to, c)}
                                data-testid={`mega-city-${slug(c)}`}
                                className="group/c text-[13px] text-[#0b2545]/75 hover:text-[var(--yellow-2)] inline-flex items-center gap-1.5 transition-all"
                              >
                                <span className="w-1 h-1 rounded-full bg-gray-300 group-hover/c:bg-[var(--yellow-2)] transition-colors" />
                                <span className="group-hover/c:translate-x-0.5 transition-transform">{c}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <Link
                          to={withQ(to, g.state)}
                          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#0b2545]/80 hover:text-[var(--yellow-2)] transition-all"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[var(--yellow-2)]" />
                          Explore
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Right column removed — feature/video disabled; hidden to reclaim space */}
          <div className="hidden">
            {false && mega.feature && (
              <a href="#packages" className="group/feat block relative rounded-lg overflow-hidden ring-1 ring-gray-200/60 hover:ring-[#f59e0b]/60 transition">
                <img
                  src={resolveAssetPath(mega.feature.image)}
                  alt={mega.feature.title}
                  className={`w-full ${denseLayout ? "h-28" : "h-36"} object-cover group-hover/feat:scale-110 transition-transform duration-700`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                  <span className="bg-[var(--yellow)] text-[var(--navy)] text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                    Featured
                  </span>
                  <span className="bg-white/90 text-[var(--navy)] text-[9px] font-semibold inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded">
                    <Clock className="w-2.5 h-2.5" /> Best Apr-Sep
                  </span>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                  <div className={`${denseLayout ? "text-sm" : "text-base"} font-serif-display leading-tight`}>
                    {mega.feature.title}
                  </div>
                  <div className="text-[10px] text-white/90 mt-0.5 flex items-center gap-1">
                    {mega.feature.subtitle}
                    <ArrowRight className="w-3 h-3 group-hover/feat:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </a>
            )}
            {false && mega.video && (
              <a
                href={`https://www.youtube.com/watch?v=${mega.video.youtubeId}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`group/vid relative rounded-lg overflow-hidden flex-1 ${denseLayout ? "min-h-[100px]" : "min-h-[120px]"} ring-1 ring-gray-200/60 hover:ring-[#f59e0b]/60 transition`}
              >
                <img
                  src={resolveAssetPath(mega.video.thumbnail)}
                  alt={mega.video.title}
                  className="w-full h-full object-cover group-hover/vid:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-[#0b2545]/60 group-hover/vid:bg-[#0b2545]/40 transition-colors flex flex-col items-center justify-center text-white text-center px-2">
                  <span className="relative mb-2">
                    <span className="absolute inset-0 bg-[#fcd34d]/40 rounded-full blur-md scale-150 group-hover/vid:scale-[1.8] transition-transform" />
                    <span className="relative w-11 h-11 rounded-full bg-[var(--yellow)] flex items-center justify-center group-hover/vid:scale-110 transition-transform shadow-lg">
                      <Play className="w-3.5 h-3.5 text-[var(--navy)] fill-[var(--navy)] ml-0.5" />
                    </span>
                  </span>
                  <div className={`${denseLayout ? "text-[12px]" : "text-sm"} font-serif-display leading-tight`}>
                    {mega.video.title}
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[var(--yellow)] mt-0.5">
                    ▸ Watch story
                  </div>
                </div>
              </a>
            )}
          </div>
        </div>

          <MegaFooter />
        </div>
      </div>
    </div>
  );
};

// ───────────────────────── Themes Mega
const ThemesMega = ({ mega, promo, to = "/" }) => {
  return (
    <div
      data-testid="mega-panel-themes"
      className="invisible pointer-events-none opacity-0 translate-y-1 group-hover:visible group-hover:pointer-events-auto group-hover:opacity-100 group-hover:translate-y-0 absolute left-0 right-0 top-full transition-all duration-200 z-50"
    >
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
        <div className="bg-white text-[var(--navy)] shadow-[0_30px_60px_-15px_rgba(11,37,69,0.4)] rounded-b-xl border-t-[3px] border-[var(--yellow-2)] overflow-hidden">
        {promo && <PromoRibbon>{promo}</PromoRibbon>}

        <div className="grid grid-cols-12 min-h-[420px]">
          <div className="col-span-8 p-8 bg-gradient-to-br from-white via-white to-[#fffaf0]">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[var(--yellow-2)]" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--yellow-2)] font-bold">
                {mega.eyebrow}
              </div>
            </div>
            <ul className="grid grid-cols-3 gap-x-6 gap-y-3">
              {mega.items.map((item) => {
                const Icon = THEME_ICONS[item.icon] || Compass;
                return (
                  <li key={item.label}>
                    <Link
                      to={withItem(to, item.label)}
                      data-testid={`mega-item-${slug(item.label)}`}
                      className="group/li flex items-center gap-3 py-2.5 px-2 rounded-md hover:bg-[#fffaf0] transition-all"
                    >
                      <span className="relative w-10 h-10 rounded-full bg-[#f6f7fb] group-hover/li:bg-[var(--yellow)] flex items-center justify-center transition-colors shrink-0 ring-1 ring-gray-200 group-hover/li:ring-[var(--yellow-2)]">
                        <Icon className="w-4 h-4 text-[var(--navy)]" strokeWidth={1.6} />
                      </span>
                      <span className="text-[13.5px] font-medium text-[var(--navy)] group-hover/li:text-[var(--yellow-2)] transition-colors flex-1">
                        {item.label}
                      </span>
                      {item.badge && (
                        <span className="bg-gradient-to-r from-[var(--red)] to-[#c8101f] text-white text-[8px] font-bold uppercase px-1.5 py-0.5 rounded shadow-sm whitespace-nowrap">
                          {item.badge}
                        </span>
                      )}
                      <ArrowRight className="w-3.5 h-3.5 text-[var(--yellow-2)] opacity-0 group-hover/li:opacity-100 group-hover/li:translate-x-0.5 transition-all" />
                    </Link>
                  </li>
                );
              })}
            </ul>

            <div className="mt-6 flex items-center gap-6">
              <Link
                to={to}
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--navy)] hover:text-[var(--yellow-2)] transition border-b border-[#f59e0b]/30 hover:border-[var(--yellow-2)] pb-1"
              >
                Explore all options <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <span className="text-[11px] text-gray-500 inline-flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-[var(--yellow-2)]" />
                Free cancellation · Best-price guarantee
              </span>
            </div>
          </div>

          <div className="col-span-4 bg-[#f8fafc] p-5 grid grid-rows-2 gap-4 border-l border-gray-100">
            {mega.features && mega.features[0] && (
              <Link
                to={to}
                className="group/feat relative rounded-lg overflow-hidden block ring-1 ring-gray-200/60 hover:ring-[#f59e0b]/60 transition"
              >
                <img
                  src={resolveAssetPath(mega.features[0].image)}
                  alt={mega.features[0].title}
                  loading="lazy"
                  className="w-full h-full object-cover absolute inset-0 group-hover/feat:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                <div className="absolute top-2 left-2 bg-[var(--yellow)] text-[var(--navy)] text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                  Featured
                </div>
                <div className="relative h-full min-h-[140px] flex flex-col justify-end p-4 text-white">
                  <div className="font-serif-display text-lg flex items-center gap-2 group-hover/feat:text-[var(--yellow)] transition">
                    {mega.features[0].title}
                    <ArrowRight className="w-4 h-4 group-hover/feat:translate-x-0.5 transition-transform" />
                  </div>
                  <p className="text-[11px] text-white/85 mt-1 leading-snug">
                    {mega.features[0].description}
                  </p>
                </div>
              </Link>
            )}
            {false && mega.video && (
              <a
                href={`https://www.youtube.com/watch?v=${mega.video.youtubeId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group/vid relative rounded-lg overflow-hidden block ring-1 ring-gray-200/60 hover:ring-[#f59e0b]/60 transition"
              >
                <img
                  src={resolveAssetPath(mega.video.thumbnail)}
                  alt={mega.video.title}
                  className="w-full h-full object-cover absolute inset-0 group-hover/vid:scale-110 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-[#0b2545]/65 group-hover/vid:bg-[#0b2545]/45 transition-colors flex flex-col items-center justify-center text-white text-center px-3 min-h-[140px]">
                  <span className="relative mb-2">
                    <span className="absolute inset-0 bg-[#fcd34d]/40 rounded-full blur-md scale-150 group-hover/vid:scale-[1.8] transition-transform" />
                    <span className="relative w-12 h-12 rounded-full bg-[var(--yellow)] flex items-center justify-center group-hover/vid:scale-110 transition-transform shadow-lg">
                      <Play className="w-4 h-4 text-[var(--navy)] fill-[var(--navy)] ml-0.5" />
                    </span>
                  </span>
                  <div className="font-serif-display text-base leading-tight">
                    {mega.video.title}
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-[var(--yellow)] mt-1">
                    ▸ Watch story
                  </div>
                </div>
              </a>
            )}
          </div>
        </div>

          <MegaFooter />
        </div>
      </div>
    </div>
  );
};


// ───────────────────────── Country flags marquee
const FlagsMarquee = () => {
  const flags = [...COUNTRY_FLAGS, ...COUNTRY_FLAGS];
  return (
    <div
      data-testid="flags-marquee"
      className="relative overflow-hidden flex-1 max-w-md mr-auto"
      aria-hidden
    >
      <div className="flex w-max animate-marquee-slow gap-6 items-center">
        {flags.map((f, i) => (
          <span
            key={`${f.code}-${i}`}
            className="flex items-center gap-1.5 shrink-0"
            title={f.name}
          >
            <img
              src={resolveAssetPath(`/assets/flags/${f.code}.png`)}
              width="20"
              height="14"
              alt=""
              loading="lazy"
              className="rounded-sm shadow-sm"
            />
            <span className="text-[11px] text-gray-500">{f.name}</span>
          </span>
        ))}
      </div>
      <div className="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
    </div>
  );
};

// ───────────────────────── Header
export const Header = () => {
  const [open, setOpen] = useState(false);
  const [closedMega, setClosedMega] = useState(null);
  const navigate = useNavigate();

  return (
    <header data-testid="site-header" id="top" className="bg-white border-b border-gray-200 sticky top-0 z-50">
      {/* Top utility row with flags + links */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6 h-10 flex items-center gap-5 text-[12px] text-gray-600">
          <FlagsMarquee />
          {TOP_UTILITY.map((l, i) => (
            <span key={l.label} className="hidden sm:flex items-center gap-5 relative z-10">
              <Link to={l.href} data-testid={`top-util-${i}`} className="cursor-pointer font-medium hover:text-[var(--red)] hover:underline underline-offset-4 transition-colors">
                {l.label}
              </Link>
              {i < TOP_UTILITY.length - 1 && <span className="text-gray-300" aria-hidden>|</span>}
            </span>
          ))}
          <div className="hidden md:flex items-center gap-3 ml-3 text-gray-500">
            <a href={SOCIAL_LINKS.whatsapp} aria-label="whatsapp" target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4 hover:text-green-600" /></a>
            <a href={SOCIAL_LINKS.facebook} aria-label="facebook" target="_blank" rel="noreferrer"><Facebook className="w-4 h-4 hover:text-blue-600" /></a>
            <a href={SOCIAL_LINKS.instagram} aria-label="instagram" target="_blank" rel="noreferrer"><Instagram className="w-4 h-4 hover:text-pink-600" /></a>
            <a href={SOCIAL_LINKS.twitter} aria-label="twitter" target="_blank" rel="noreferrer"><Twitter className="w-4 h-4 hover:text-sky-500" /></a>
            <a href={SOCIAL_LINKS.youtube} aria-label="youtube" target="_blank" rel="noreferrer" data-testid="header-youtube"><Youtube className="w-4 h-4 hover:text-red-600" /></a>
            <a href={SOCIAL_LINKS.linkedin} aria-label="linkedin" target="_blank" rel="noreferrer"><Linkedin className="w-4 h-4 hover:text-blue-700" /></a>
          </div>
        </div>
      </div>

      {/* Main header row */}
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-0 flex items-center gap-6">
        <a href="#top" className="shrink-0 flex items-center -my-2" data-testid="header-logo" onClick={(e) => { e.preventDefault(); navigate("/"); }}>
          <img
            src={resolveAssetPath(BRAND.logo)}
            alt="Royal Tours Group"
            className="h-20 w-20 md:h-24 md:w-24 object-contain"
          />
        </a>

        {/* Search */}
        <div className="flex-1 max-w-2xl hidden md:block">
          <SearchBox />
        </div>

        {/* Value props */}
        <div className="hidden xl:flex items-center gap-5">
          {VALUE_PROPS.map((vp) => {
            const Icon = VP_ICONS[vp.icon] || Trophy;
            return (
              <Link key={vp.title} to={vp.href} data-testid={`value-prop-${vp.icon.toLowerCase()}`} className="group flex items-center gap-2 cursor-pointer rounded-md px-1.5 py-1 hover:bg-[#fff8e7] transition-colors">
                <Icon className="w-5 h-5 text-[var(--yellow-2)] group-hover:scale-110 transition-transform" />
                <div className="leading-tight">
                  <div className="text-[12px] font-semibold text-[var(--navy)] group-hover:text-[var(--red)] transition-colors">
                    {vp.title}
                  </div>
                  <div className="text-[11px] text-gray-500">{vp.subtitle}</div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Phone */}
        <a
          href={`tel:${BRAND.phone.replace(/[^0-9+]/g, "")}`}
          data-testid="header-phone"
          className="hidden lg:flex items-center gap-2 text-[var(--navy)] font-semibold text-sm whitespace-nowrap hover:text-[var(--yellow-2)]"
        >
          <Phone className="w-4 h-4" />
          {BRAND.phone}
        </a>

        <button
          type="button"
          data-testid="header-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden ml-auto p-2 text-[var(--navy)]"
          aria-label="menu"
          aria-expanded={open}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Nav bar (navy) */}
      <nav className="hidden lg:block bg-[var(--navy)] text-white relative">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6 flex items-stretch">
          <div className="hidden lg:flex items-stretch flex-1">
            {NAV_LINKS.map((l, idx) => {
              const alignRight = idx >= NAV_LINKS.length - 2;
              return (
              <div
                key={l.label}
                onClick={(e) => e.target.closest("a") && setClosedMega(l.label)}
                onMouseLeave={() => setClosedMega(null)}
                className={`group ${l.mega ? "static" : "relative"} ${closedMega === l.label ? "[&>div]:!invisible [&>div]:!opacity-0 [&>div]:!pointer-events-none" : ""}`}
              >
                <button
                  type="button"
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => l.route && navigate(l.route)}
                  className="h-full flex items-center gap-1 px-3.5 text-[13px] font-medium text-white/95 hover:text-[var(--yellow)] hover:bg-white/5 transition py-3.5 whitespace-nowrap"
                >
                  {l.label}
                  {(l.mega || l.dropdown) && (
                    <ChevronDown className="w-3 h-3 transition-transform group-hover:rotate-180" />
                  )}
                </button>

                {l.mega?.kind === "destinations" && <DestinationsMega mega={l.mega} alignRight={alignRight} to={l.route} />}
                {l.mega?.kind === "themes" && <ThemesMega mega={l.mega} alignRight={alignRight} to={l.route} />}

                {l.dropdown && !l.mega && (
                  <div className="absolute top-full left-0 mt-0 min-w-[220px] bg-white text-[var(--navy)] shadow-2xl rounded-md py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    {l.dropdown.map((d) => (
                      <a
                        key={d}
                        href="#"
                        className="block px-4 py-2 text-sm hover:bg-gray-50 hover:text-[var(--yellow-2)]"
                      >
                        {d}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {/* Premium CTA — compact */}
          <a
            href="/contact"
            onClick={(e) => { e.preventDefault(); navigate("/contact"); }}
            data-testid="nav-cta-book"
            className="hidden lg:inline-flex w-full justify-center lg:w-auto lg:ml-auto group/cta self-center items-center gap-2 bg-gradient-to-b from-[var(--yellow)] to-[#e6b945] hover:from-white hover:to-white text-[var(--navy)] font-semibold text-[11px] uppercase tracking-[0.14em] px-4 py-2 my-1.5 rounded-sm transition-colors whitespace-nowrap"
          >
            <span>Speak to an Expert</span>
            <ArrowRight className="w-3.5 h-3.5 group-hover/cta:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </nav>

      {/* Mobile nav */}
      {open && <MobileNav onClose={() => setOpen(false)} navigate={navigate} />}
    </header>
  );
};

// ───────────────────────── Mobile navigation drawer
const MobileNav = ({ onClose, navigate }) => {
  const [expanded, setExpanded] = useState(null);

  const go = (route) => {
    if (route) navigate(route);
    onClose();
  };

  const subItemsFor = (l) => {
    if (l.mega?.kind === "destinations") return l.mega.regions.map((r) => ({ label: r.name, to: withQ(l.route, r.name) }));
    if (l.mega?.kind === "themes") return l.mega.items.map((it) => ({ label: it.label, to: withItem(l.route, it.label) }));
    return null;
  };

  return (
    <>
      <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={onClose} data-testid="mobile-nav-overlay" />
      <div
        data-testid="mobile-nav-panel"
        className="lg:hidden fixed top-0 right-0 bottom-0 w-[86%] max-w-sm bg-white z-50 shadow-2xl flex flex-col animate-[fade-up_0.25s_ease]"
      >
        <div className="flex items-center justify-between px-5 h-16 border-b border-gray-100 bg-[var(--navy)] text-white">
          <span className="font-serif-display text-lg">Menu</span>
          <button type="button" onClick={onClose} aria-label="close menu" data-testid="mobile-nav-close" className="p-2">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="px-5 py-4 border-b border-gray-100">
          <SearchBox variant="mobile" onDone={onClose} />
        </div>

        <nav className="flex-1 overflow-y-auto thin-scroll py-2">
          {NAV_LINKS.map((l, idx) => {
            const subs = subItemsFor(l);
            const isOpen = expanded === idx;
            return (
              <div key={l.label} className="border-b border-gray-50">
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => go(l.route)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                    className="flex-1 text-left px-5 py-3.5 text-[15px] font-medium text-[var(--navy)] hover:text-[var(--yellow-2)] transition"
                  >
                    {l.label}
                  </button>
                  {subs && (
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : idx)}
                      aria-label={`toggle ${l.label}`}
                      data-testid={`mobile-nav-expand-${idx}`}
                      className="px-4 py-3.5 text-[#0b2545]/60"
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
                {subs && isOpen && (
                  <div className="bg-[var(--soft)] px-5 py-2">
                    {subs.map((s) => (
                      <button
                        key={s.label}
                        type="button"
                        onClick={() => go(s.to)}
                        data-testid={`mobile-sub-${slug(s.label)}`}
                        className="block w-full text-left py-2 text-[13.5px] text-[#0b2545]/75 hover:text-[var(--yellow-2)] transition"
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-gray-100 p-5 space-y-3">
          <div className="flex gap-2">
            <button type="button" onClick={() => go("/about")} data-testid="mobile-nav-about" className="flex-1 text-center border border-gray-200 rounded-md py-2.5 text-[13px] font-medium text-[var(--navy)] hover:border-[var(--navy)] transition">About us</button>
            <button type="button" onClick={() => go("/contact")} data-testid="mobile-nav-contact" className="flex-1 text-center border border-gray-200 rounded-md py-2.5 text-[13px] font-medium text-[var(--navy)] hover:border-[var(--navy)] transition">Contact us</button>
          </div>
          <button
            type="button"
            onClick={() => go("/contact")}
            data-testid="mobile-nav-cta"
            className="w-full flex items-center justify-center gap-2 bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-semibold text-[13px] uppercase tracking-[0.14em] py-3 rounded-md transition-colors"
          >
            Speak to an Expert <ArrowRight className="w-4 h-4" />
          </button>
          <a
            href={`tel:${BRAND.phone.replace(/[^0-9+]/g, "")}`}
            className="flex items-center justify-center gap-2 text-[var(--navy)] font-semibold text-sm"
          >
            <Phone className="w-4 h-4 text-[var(--yellow-2)]" /> {BRAND.phone}
          </a>
          <div className="flex items-center justify-center gap-4 pt-1 text-gray-500">
            <a href={SOCIAL_LINKS.whatsapp} aria-label="whatsapp" target="_blank" rel="noreferrer"><MessageCircle className="w-5 h-5 hover:text-green-600" /></a>
            <a href={SOCIAL_LINKS.facebook} aria-label="facebook" target="_blank" rel="noreferrer"><Facebook className="w-5 h-5 hover:text-blue-600" /></a>
            <a href={SOCIAL_LINKS.instagram} aria-label="instagram" target="_blank" rel="noreferrer"><Instagram className="w-5 h-5 hover:text-pink-600" /></a>
            <a href={SOCIAL_LINKS.linkedin} aria-label="linkedin" target="_blank" rel="noreferrer"><Linkedin className="w-5 h-5 hover:text-blue-700" /></a>
          </div>
        </div>
      </div>
    </>
  );
};
